import { NumberFlow } from '@/components/ui/number-flow'
import { cn } from '@/lib/utils'

type MoneySize = 'sm' | 'md' | 'lg' | 'xl'

export interface MoneyProps {
  /** Částka. Ve výchozím stavu v haléřích (centech), viz `minor`. */
  amount: number | string | null | undefined
  currency?: string | null
  /** true = `amount` je v haléřích (výchozí, tak vrací API), false = v korunách */
  minor?: boolean
  size?: MoneySize
  /** Zobrazit znaménko + u kladných částek */
  signed?: boolean
  /** Obarvit záporné částky (danger) a kladné při `signed` (success) */
  tone?: boolean
  /** Nenápadná animace přes NumberFlow: při změně hodnoty číslice přetočí, při prvním zobrazení se částka jemně objeví */
  animate?: boolean
  className?: string
}

const sizeClass: Record<MoneySize, { whole: string; fraction: string }> = {
  sm: { whole: 'text-[13px]', fraction: 'text-[11px]' },
  md: { whole: 'text-sm', fraction: 'text-xs' },
  lg: { whole: 'text-2xl tracking-[-0.02em]', fraction: 'text-base' },
  xl: { whole: 'text-[40px] leading-none tracking-[-0.03em]', fraction: 'text-xl' },
}

/**
 * Částka ve stylu Mercury: celé číslo plným písmem, haléře menším a tlumeným.
 * Vždy tabulkové číslice a správná měna dokladu.
 */
export const Money = ({
  amount,
  currency = 'CZK',
  minor = true,
  size = 'md',
  signed = false,
  tone = false,
  animate = false,
  className,
}: MoneyProps) => {
  const missing = amount === null || amount === undefined || amount === '' || !Number.isFinite(Number(amount))

  if (missing) {
    return <span className={cn('text-muted-foreground', className)}>-</span>
  }

  const value = minor ? Number(amount) / 100 : Number(amount)

  if (animate) {
    // celé koruny a haléře jako dvě samostatná NumberFlow čísla: každé má vlastní velikost písma,
    // takže ořezová maska točících se číslic sedí a nad haléři nezůstávají artefakty
    const negative = value < 0
    const abs = Math.abs(value)
    let whole = Math.floor(abs)
    let cents = Math.round((abs - whole) * 100)
    if (cents === 100) {
      whole += 1
      cents = 0
    }
    const affix = new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency: currency || 'CZK',
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(1)
    const symbolIndex = affix.findIndex((p) => p.type === 'currency')
    const integerIndex = affix.findIndex((p) => p.type === 'integer')
    const symbol = affix[symbolIndex]?.value ?? ''
    const symbolFirst = symbolIndex > -1 && symbolIndex < integerIndex
    const sign = negative ? '−' : signed && value > 0 ? '+' : ''
    const sz = sizeClass[size]

    return (
      <span
        className={cn(
          'tabular inline-flex items-baseline whitespace-nowrap font-medium motion-safe:animate-[money-in_320ms_ease-out]',
          tone && negative && 'text-danger',
          tone && signed && value > 0 && 'text-success',
          className,
        )}
      >
        {symbolFirst && <span className={cn(sz.fraction, 'mr-0.5 text-muted-foreground')}>{symbol}</span>}
        <span className={sz.whole}>
          {sign}
          <NumberFlow value={whole} format={{ maximumFractionDigits: 0, useGrouping: true }} />
        </span>
        <span className={cn(sz.fraction, 'text-muted-foreground', tone && (negative || (signed && value > 0)) && 'text-current')}>
          ,
          <NumberFlow value={cents} format={{ minimumIntegerDigits: 2, useGrouping: false }} />
        </span>
        {!symbolFirst && symbol && <span className={cn(sz.fraction, 'ml-1 text-muted-foreground')}>{symbol}</span>}
      </span>
    )
  }

  const parts = new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: currency || 'CZK',
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).formatToParts(Math.abs(value))

  const fraction = parts.find((p) => p.type === 'fraction')?.value
  const decimal = parts.find((p) => p.type === 'decimal')?.value ?? ','
  const before: string[] = []
  const after: string[] = []
  let seenFraction = false
  for (const p of parts) {
    if (p.type === 'fraction' || p.type === 'decimal') {
      seenFraction = true
      continue
    }
    ;(seenFraction ? after : before).push(p.value)
  }

  const negative = value < 0
  const sign = negative ? '−' : signed && value > 0 ? '+' : ''
  const s = sizeClass[size]

  return (
    <span
      className={cn(
        'tabular inline-flex items-baseline whitespace-nowrap font-medium',
        tone && negative && 'text-danger',
        tone && signed && value > 0 && 'text-success',
        className,
      )}
    >
      <span className={s.whole}>
        {sign}
        {before.join('').trim()}
      </span>
      {fraction !== undefined && (
        <span className={cn(s.fraction, 'text-muted-foreground', tone && (negative || (signed && value > 0)) && 'text-current')}>
          {decimal}
          {fraction}
        </span>
      )}
      {after.join('').trim() && (
        <span className={cn(s.fraction, 'ml-1 text-muted-foreground')}>{after.join('').trim()}</span>
      )}
    </span>
  )
}
